import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, RotateCcw, GraduationCap } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/** Quiz d'intégration livreur : obligatoire avant de pouvoir passer « En ligne ».
 * 6 questions, une à la fois — 4 bonnes réponses minimum. Tentatives illimitées. */
export default function DriverQuiz() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setQuiz(null); setIdx(0); setAnswers({}); setSelected(null); setResult(null);
    try {
      const { data } = await apiClient.get("/driver/quiz");
      if (data.passed) { nav("/driver", { replace: true }); return; }
      setQuiz(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    if (user && user.role !== "driver") { nav("/", { replace: true }); return; }
    if (user && user.quiz_passed !== false) { nav("/driver", { replace: true }); return; }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const next = async () => {
    if (!selected) return;
    const q = quiz.questions[idx];
    const newAnswers = { ...answers, [q.id]: selected };
    setAnswers(newAnswers);
    setSelected(null);
    if (idx + 1 < quiz.questions.length) {
      setIdx(idx + 1);
      return;
    }
    // Dernière question → soumission
    setBusy(true);
    try {
      const { data } = await apiClient.post("/driver/quiz/submit", { answers: newAnswers });
      setResult(data);
      if (data.passed) {
        await refreshUser();
        setTimeout(() => nav("/driver", { replace: true }), 2800);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="quiz-result">
        {result.passed ? (
          <>
            <div className="w-20 h-20 rounded-full bg-[#E7F6EE] text-brand-green grid place-items-center mx-auto mb-5">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h1 className="font-display text-3xl font-semibold mb-2" data-testid="quiz-success-title">{t("dquiz.successTitle")}</h1>
            <p className="text-mute mb-2"><b>{t("dquiz.score", { score: result.score, total: result.total })}</b> — {t("dquiz.nowActive")} <b className="text-brand-green">{t("dquiz.active")}</b>.</p>
            <p className="text-sm text-mute flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> {t("dquiz.redirect")}
            </p>
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-[#FFF5F6] text-brand-red grid place-items-center mx-auto mb-5">
              <XCircle className="w-10 h-10" />
            </div>
            <h1 className="font-display text-3xl font-semibold mb-2" data-testid="quiz-fail-title">{t("dquiz.failTitle")}</h1>
            <p className="text-mute mb-6"><b>{t("dquiz.score", { score: result.score, total: result.total })}</b> — {t("dquiz.failText", { n: result.pass_score })}</p>
            <button onClick={load} data-testid="quiz-retry-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] text-white px-6 py-3 font-semibold">
              <RotateCcw className="w-4 h-4" /> {t("dquiz.retry")}
            </button>
          </>
        )}
      </div>
    );
  }

  if (!quiz) {
    return <div className="grid place-items-center min-h-[50vh] text-mute"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const q = quiz.questions[idx];
  const isLast = idx + 1 === quiz.questions.length;

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="driver-quiz-page">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-full bg-[#FFF5F6] text-brand-red grid place-items-center mx-auto mb-3">
          <GraduationCap className="w-7 h-7" />
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-1">{t("dquiz.kicker")}</div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold">{t("dquiz.title")}</h1>
        <p className="text-sm text-mute mt-2">{t("dquiz.sub", { n: quiz.pass_score })}
          {quiz.attempts > 0 && <span className="block mt-1">{t("dquiz.attempt", { n: quiz.attempts + 1 })}</span>}</p>
      </div>

      {/* Progression */}
      <div className="flex gap-1.5 mb-6" data-testid="quiz-progress">
        {quiz.questions.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i < idx ? "bg-brand-green" : i === idx ? "bg-brand-red" : "bg-sand"}`} />
        ))}
      </div>

      <div className="bg-white border border-sand rounded-3xl p-6 sm:p-8">
        <div className="text-xs uppercase tracking-widest text-mute mb-2">{t("dquiz.question", { i: idx + 1, n: quiz.questions.length })}</div>
        <h2 className="font-display text-lg sm:text-xl font-semibold mb-5" data-testid="quiz-question-text">{q.text}</h2>
        <div className="space-y-2.5">
          {q.options.map((o) => (
            <button key={o.id} type="button" onClick={() => setSelected(o.id)}
              data-testid={`quiz-option-${o.id}`}
              className={`w-full text-left rounded-xl border px-4 py-3.5 text-sm font-medium transition-colors ${
                selected === o.id ? "border-brand-red bg-[#FFF5F6] text-brand-red" : "border-sand bg-white hover:border-brand-red/50"
              }`}>
              {o.text}
            </button>
          ))}
        </div>
        <button onClick={next} disabled={!selected || busy} data-testid="quiz-next-btn"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink hover:bg-black disabled:opacity-40 text-white px-6 py-3.5 font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {isLast ? t("dquiz.submit") : t("dquiz.nextBtn")}
        </button>
      </div>

      <p className="text-center text-xs text-mute mt-6">
        {t("dquiz.footer")}
      </p>
    </div>
  );
}
